import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BellRing, Check, FileText, MessagesSquare, Waypoints } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1605106702842-01a887a31122?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMGNsZWFufGVufDB8fHx8MTc4NzU1ODQ3M3ww&ixlib=rb-4.1.0&q=85";
const TRUST_IMG =
  "https://images.pexels.com/photos/8408550/pexels-photo-8408550.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ICONS = [FileText, MessagesSquare, BellRing];

const FALLBACK = {
  badge: "Customer Journey Management",
  hero_title: "Every customer conversation, in one calm place.",
  hero_subtitle:
    "Build forms, collect uploads, and keep every reply in a transparent thread your customers can revisit anytime.",
  cta_primary: "Start free",
  cta_secondary: "View pricing",
  features: [
    { title: "Form builder", description: "Build branded intake forms in minutes. Customers upload files right alongside their answers." },
    { title: "Conversation hub", description: "Every submission opens a shared thread. Reply from your dashboard, customers reply from a magic link." },
    { title: "Email & SMS alerts", description: "Both sides are notified instantly. Bring your own SMTP and Twilio, or use the platform defaults." },
  ],
  stat_customers: "500+",
  stat_label: "businesses trust CreateForms.app",
  footer_text: "CreateForms.app — Customer Journey Management",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12 } }),
};

export default function Home() {
  const [cms, setCms] = useState(FALLBACK);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/public/cms").then(({ data }) => data?.hero_title && setCms({ ...FALLBACK, ...data })).catch(() => {});
    api.get("/public/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = cms.seo_title || cms.site_name || "CreateForms.app";
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", cms.seo_description || cms.hero_subtitle);
    setMeta("keywords", cms.seo_keywords);
  }, [cms]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="home-page">
      <header className="fixed top-0 inset-x-0 z-30 bg-white/70 backdrop-blur-xl border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="nav-logo">
            <Waypoints className="h-5 w-5" />
            <span className="font-heading font-bold tracking-tight">CreateForms.app</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-600">
            <a href="#features" className="hover:text-neutral-900 transition-colors duration-150" data-testid="nav-features-link">Features</a>
            <a href="#pricing" className="hover:text-neutral-900 transition-colors duration-150" data-testid="nav-pricing-link">Pricing</a>
            <Link to="/customer/login" className="hover:text-neutral-900 transition-colors duration-150" data-testid="nav-customer-login-link">Customer login</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost" className="rounded-full">Log in</Button>
            </Link>
            <Link to="/register" data-testid="nav-get-started-link">
              <Button className="rounded-full px-5 transition-transform duration-150 hover:-translate-y-0.5">Get started</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="pt-36 pb-24 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <span className="inline-block text-[11px] uppercase tracking-[0.2em] border border-[#E5E5E5] bg-white rounded-full px-4 py-1.5 text-neutral-600" data-testid="hero-badge">
              {cms.badge}
            </span>
            <h1 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02]" data-testid="hero-title">
              {cms.hero_title}
            </h1>
            <p className="mt-6 text-base md:text-lg text-neutral-500 max-w-lg leading-relaxed" data-testid="hero-subtitle">
              {cms.hero_subtitle}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" data-testid="hero-cta-primary">
                <Button className="rounded-full px-8 h-12 text-base transition-transform duration-150 hover:-translate-y-0.5">
                  {cms.cta_primary} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#pricing" data-testid="hero-cta-secondary">
                <Button variant="outline" className="rounded-none h-12 px-8 text-base border-neutral-900">
                  {cms.cta_secondary}
                </Button>
              </a>
            </div>
            <div className="mt-14 flex items-center gap-4">
              <span className="text-4xl font-black font-heading" data-testid="hero-stat">{cms.stat_customers}</span>
              <span className="text-sm text-neutral-500 max-w-[160px] leading-snug">{cms.stat_label}</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
            <div className="rounded-3xl overflow-hidden border border-[#E5E5E5]">
              <img src={HERO_IMG} alt="Abstract journey" className="w-full h-[420px] object-cover" data-testid="hero-image" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-[#E5E5E5] rounded-2xl p-5 w-64 shadow-sm">
              <div className="flex items-center gap-2 text-xs text-neutral-500 uppercase tracking-widest">
                <span className="h-2 w-2 rounded-full bg-emerald-600" /> Live thread
              </div>
              <p className="mt-2 text-sm font-medium">"Your documents look great — approved!"</p>
              <p className="text-xs text-neutral-400 mt-1">Business · just now</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="features" className="py-24 px-6 border-t border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto">
          <motion.h2 initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="text-3xl md:text-4xl font-black max-w-xl" data-testid="features-heading">
            Everything between "hello" and "here's your file"
          </motion.h2>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {(cms.features || []).map((f, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <motion.div
                  key={f.title}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className={`bg-white border border-[#E5E5E5] rounded-2xl p-8 hover:shadow-sm transition-shadow duration-200 ${i === 0 ? "md:col-span-2" : ""}`}
                  data-testid={`feature-card-${i}`}
                >
                  <div className="h-11 w-11 rounded-full bg-neutral-900 text-white flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-6 text-xl font-bold">{f.title}</h3>
                  <p className="mt-3 text-sm text-neutral-500 leading-relaxed max-w-md">{f.description}</p>
                </motion.div>
              );
            })}
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={3} className="md:col-span-3 rounded-2xl overflow-hidden border border-[#E5E5E5] relative h-56">
              <img src={TRUST_IMG} alt="Calm workspace" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-neutral-900/55 flex items-end p-8">
                <p className="text-white text-xl font-heading font-bold max-w-lg">
                  Full transparency — both sides always see the whole conversation and every upload.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24 px-6 border-t border-[#E5E5E5] bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black" data-testid="pricing-heading">Simple plans, honest pricing</h2>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {plans.map((p) => (
              <div key={p.id} className="border border-[#E5E5E5] rounded-2xl p-8 bg-[#FAFAFA] flex flex-col" data-testid={`plan-card-${p.id}`}>
                <h3 className="text-lg font-bold">{p.name}</h3>
                <div className="mt-4">
                  <span className="text-4xl font-black font-heading">£{p.price_monthly}</span>
                  <span className="text-sm text-neutral-500">/month</span>
                </div>
                <ul className="mt-6 space-y-2.5 text-sm text-neutral-600 flex-1">
                  <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0" /> {p.features?.max_forms >= 999 ? "Unlimited" : p.features?.max_forms} form(s)</li>
                  <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0" /> {(p.features?.max_submissions_month || 0).toLocaleString()} submissions / month</li>
                  <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0" /> File uploads</li>
                  {p.features?.sms && <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0" /> SMS notifications</li>}
                  {p.features?.custom_smtp && <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0" /> Custom SMTP & Twilio</li>}
                </ul>
                <Link to="/register" className="mt-8" data-testid={`plan-cta-${p.id}`}>
                  <Button className="w-full rounded-full transition-transform duration-150 hover:-translate-y-0.5" variant={p.price_monthly === 0 ? "outline" : "default"}>
                    Choose {p.name}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5E5E5] py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Waypoints className="h-4 w-4" />
            <span className="text-sm text-neutral-500" data-testid="footer-text">{cms.footer_text}</span>
          </div>
          <Link to="/customer/login" className="text-sm text-neutral-500 hover:text-neutral-900" data-testid="footer-customer-login">
            Are you a customer? Get a login link
          </Link>
        </div>
      </footer>
    </div>
  );
}
